import { useEffect, useState } from 'react';
import Iconify from './iconify';
import { Box, IconButton } from '@mui/material';

interface AnimatedListProps<T> {
  list: T[];
  onDelete: (id: number | string) => void;
  renderItem: (item: T) => React.ReactNode;
  getItemId: (item: T) => number | string;
  containerStyle?: React.CSSProperties;
  itemStyle?: React.CSSProperties;
  showDeleteButton?: boolean;
  deleteIcon?: string;
  gridColumns?: string;
}

export const AnimatedList = <T,>({
  list,
  onDelete,
  renderItem,
  getItemId,
  containerStyle,
  itemStyle,
  showDeleteButton = true,
  deleteIcon = 'eva:trash-2-outline',
  gridColumns = '1fr 60px',
}: AnimatedListProps<T>) => {
  const [visibleItems, setVisibleItems] = useState<Set<number | string>>(
    new Set()
  );
  const [removingItems, setRemovingItems] = useState<Set<number | string>>(
    new Set()
  );
  const [hoveredItem, setHoveredItem] = useState<number | string | null>(null);

  // Fade in items on mount and when new items are added
  useEffect(() => {
    const timer = setTimeout(() => {
      const newVisibleItems = new Set<number | string>();
      list.forEach((item) => {
        const id = getItemId(item);
        newVisibleItems.add(id);
      });
      setVisibleItems(newVisibleItems);
    }, 50);
    return () => clearTimeout(timer);
  }, [list, getItemId]);

  const handleDelete = (id: number | string) => {
    setRemovingItems((prev) => new Set(prev).add(id));
    // Wait for animation to complete before calling onDelete
    setTimeout(() => {
      onDelete(id);
      setRemovingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 350);
  };

  const getItemStyle = (item: T) => {
    const id = getItemId(item);
    const isVisible = visibleItems.has(id);
    const isRemoving = removingItems.has(id);
    const isHovered = hoveredItem === id;

    const baseStyle = {
      padding: '8px 16px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      backgroundColor: 'rgba(82, 134, 198, 0.05)',
      border: '1px solid rgba(82, 134, 198, 0.1)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      opacity: isVisible && !isRemoving ? 1 : 0,
      transform: isRemoving
        ? 'translateX(-100%) scale(0.8)'
        : isVisible
        ? 'translateX(0) scale(1)'
        : 'translateX(20px) scale(0.95)',
      maxHeight: isRemoving ? '0' : '80px',
      overflow: 'hidden',
      marginBottom: isRemoving ? '0' : '8px',
      paddingTop: isRemoving ? '0' : '8px',
      paddingBottom: isRemoving ? '0' : '8px',
      ...itemStyle,
    };

    const hoverStyle = {
      backgroundColor: 'rgba(82, 134, 198, 0.08)',
      transform: 'translateY(-2px) scale(1.01)',
      boxShadow: '0 4px 12px rgba(82, 134, 198, 0.15)',
    };

    return {
      ...baseStyle,
      ...(isHovered && !isRemoving ? hoverStyle : {}),
    };
  };

  const defaultContainerStyle = {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '12px',
    padding: '16px',
    minHeight: '200px',
  };

  return (
    <div style={{ ...defaultContainerStyle, ...containerStyle }}>
      {list.map((item: T) => {
        const id = getItemId(item);
        return (
          <Box
            key={id}
            style={getItemStyle(item)}
            onMouseEnter={() => setHoveredItem(id)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: showDeleteButton ? gridColumns : '1fr',
                gap: '16px',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {/* Rendered item content */}
              {renderItem(item)}

              {/* Delete Button */}
              {showDeleteButton && (
                <IconButton
                  onClick={() => handleDelete(id)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    fontSize: '16px',
                  }}
                  sx={{
                    '&:hover': {
                      transform: 'scale(1.1)',
                      color: 'tomato',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Iconify icon={deleteIcon} size={20} />
                </IconButton>
              )}
            </div>
          </Box>
        );
      })}
    </div>
  );
};
